import { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { Button } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { staffAction } from '../../data/dataProvider';
import { ActionDialog } from '../../components/ActionDialog';
import { useCanModerate } from '../../auth/useCanModerate';

/**
 * Блокировка/разблокировка пользователя с диалогом подтверждения.
 * Работает и в списке (по record из строки), и на detail-странице.
 */
export function SuspendUserButton(): JSX.Element | null {
  const record = useRecordContext<{ id: string; status: string }>();
  const notify = useNotify();
  const refresh = useRefresh();
  const canModerate = useCanModerate();
  const [open, setOpen] = useState(false);

  if (!record || !canModerate) return null;
  if (record.status !== 'active' && record.status !== 'suspended') return null;
  const isSuspended = record.status === 'suspended';

  async function confirm(reason: string): Promise<void> {
    if (!record) return;
    try {
      if (isSuspended) {
        await staffAction('POST', `/staff/users/${record.id}/unsuspend`);
        notify('Пользователь разблокирован', { type: 'success' });
      } else {
        await staffAction('POST', `/staff/users/${record.id}/suspend`, {
          reason,
        });
        notify('Пользователь заблокирован', { type: 'success' });
      }
      setOpen(false);
      refresh();
    } catch (e) {
      notify(`Ошибка: ${(e as Error).message}`, { type: 'error' });
    }
  }

  return (
    <>
      <Button
        size="small"
        color={isSuspended ? 'success' : 'warning'}
        startIcon={isSuspended ? <LockOpenIcon /> : <BlockIcon />}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {isSuspended ? 'Разблокировать' : 'Заблокировать'}
      </Button>
      <ActionDialog
        open={open}
        title={isSuspended ? 'Разблокировать пользователя?' : 'Заблокировать пользователя?'}
        description={
          isSuspended
            ? 'Пользователь снова сможет входить и пользоваться приложением.'
            : 'Пользователь потеряет доступ к приложению. Действие обратимо — доступна разблокировка.'
        }
        confirmLabel={isSuspended ? 'Разблокировать' : 'Заблокировать'}
        confirmColor={isSuspended ? 'success' : 'warning'}
        reason={
          isSuspended ? undefined : { label: 'Причина блокировки', required: true, minLength: 5 }
        }
        onConfirm={confirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
